import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import {
  DropDownListBlurEvent,
  DropDownListChangeEvent,
  DropDownListHandle,
} from "@progress/kendo-react-dropdowns";
import DropdownOption from './DropdownOption';
import SearchableDropdown from './SearchableDropdown';
import { ProviderService } from '../../services';
import { ProviderModel } from '../../data/entities';
import { createSyntheticEvent } from '../../utils';

interface SearchableProviderDropdownProps extends PropsWithChildren {
  value?: any;
  name: string;
  locationId?: number | null;
  isInvalid?: boolean;
  onBlur?: (e: DropDownListBlurEvent) => void;
  onChange?: (e: DropDownListChangeEvent) => void;
  onSelect?: (value: DropdownOption) => void;
  placeholder?: string;
  providers?: DropdownOption[];
  defaultItem?: boolean;
}

export const SearchableProviderDropdown = React.forwardRef(
  (
    props: Readonly<SearchableProviderDropdownProps>,
    ref: React.ForwardedRef<HTMLSelectElement | null>
  ) => {
    const dropdownRef = useRef(null);
    const providerService = useMemo(()=> new ProviderService(),[])
    const [options, setOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
      if (props.providers) {
        setOptions(props.providers.sort((a, b) => a.text.localeCompare(b.text)));
        let activeRef = (ref ?? dropdownRef as any)?.current as DropDownListHandle;
        let select = activeRef?.element?.querySelector('select');
        if (select) {
          const event = new Event('select', { bubbles: true });
          Object.defineProperty(event, 'target', { writable: false, value: select });
          const syntheticEvent = createSyntheticEvent(event) as React.ChangeEvent<typeof select>;
          props.onChange?.call(this, {
            nativeEvent: event,
            syntheticEvent: syntheticEvent,
            target: activeRef,
            value: activeRef.props.value
          });
        }
      }else{
        const fetchData = async () => {
          const providers = await providerService.getAll();
          const newOptions = providers
            .filter((provider: ProviderModel) => provider.isActive)
            .map<DropdownOption>((provider: ProviderModel) => ({
              value: provider.id?.toString() ?? "",
              text: `${provider.name}`,
            }));

          setOptions(newOptions);
        };
        fetchData();
    }
    }, [props.locationId, props.providers, providerService, props.value]);

    const getValue = () => {
      if (props.value || props.value === "")
        return options.find((option) => option.value === props.value?.toString());

    }

    return (
      <SearchableDropdown
        defaultItem={props.defaultItem ? {text:"Select a Provider", value:"-1",} : undefined}
        label="Provider"
        name={props.name}
        options={options}
        value={getValue()}
        onChange={props.onChange}
        onSelect={props.onSelect}
        isInvalid={props.isInvalid}
      >
        {props.children}
      </SearchableDropdown>
    );
  }
);

export default SearchableProviderDropdown
